import { isNil } from 'lodash';
import {
  collegeMaxPlanLimits,
  ExchangeType,
  UNIT,
  NEW_ICLASS_FUND_MIN_AMOUNT,
  EXCHANGE,
} from '../../../settings/settings';
import {
  isProductType529,
  isSelectedWithAmount,
} from '../../funds/funds-utility';
import { isNewIClassFund } from '../../funds/shared/shared-fund-notes-utility';
import {
  getExchangeMinimum,
  getExchangeToDollarValue,
} from './validate.helpers';
import { coalesce, currency, sumArray } from '../../utils';
import { getWordsForFund } from '../../words';
import { isAPP } from '../../advice/advice-utility';

/**
 * Applies exchange from validation and clears or populates related errors: grossAmountError,
 *
 * @param {ExchangeFund} fund
 */
export function validateExchangeFromFund(fund) {
  const {
    equityAsOf,
    fundLimitMessage,
    grossAmount,
    grossAmountDollars,
    grossUnit,
    productType,
    redeemableFundDollars,
    redeemMaxMessage,
    selected,
    shares,
    transactionLimit,
  } = fund || {};
  if (!selected) {
    return;
  }

  delete fund.grossAmountError;

  if (grossAmount === 0 || isNil(grossAmount)) {
    return;
  }
  const exchangeFromDailyMax = transactionLimit.maximum;
  const exchangeFromDailyMin = transactionLimit.minimum;

  const percentTotal = grossAmountDollars / equityAsOf;
  const exceedsAPPWithdrawThresholdCriteria =
    (percentTotal > 0.85 || equityAsOf - grossAmountDollars < 10000) &&
    isAPP(productType);

  switch (grossUnit) {
    case UNIT.DOLLARS:
      if (
        grossAmountDollars <= exchangeFromDailyMax &&
        grossAmountDollars > redeemableFundDollars
      ) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (grossAmountDollars > exchangeFromDailyMax) {
        fund.grossAmountError = fundLimitMessage;
      } else if (grossAmountDollars < exchangeFromDailyMin) {
        fund.grossAmountError = fundLimitMessage;
      } else if (exceedsAPPWithdrawThresholdCriteria) {
        fund.grossAmountError = fundLimitMessage;
      }
      break;
    case UNIT.PERCENT:
      if (grossAmount > 100) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (
        grossAmount < 0.01 ||
        grossAmountDollars < exchangeFromDailyMin
      ) {
        fund.grossAmountError = fundLimitMessage;
      } else if (grossAmountDollars > exchangeFromDailyMax) {
        fund.grossAmountError = fundLimitMessage;
      }
      break;
    case UNIT.SHARES:
      if (grossAmount > shares) {
        fund.grossAmountError = redeemMaxMessage;
      } else if (
        grossAmount < 0.001 ||
        grossAmountDollars < exchangeFromDailyMin
      ) {
        fund.grossAmountError = fundLimitMessage;
      } else if (grossAmountDollars > exchangeFromDailyMax) {
        fund.grossAmountError = fundLimitMessage;
      }
      break;
    case UNIT.ALL_REMAINING:
    case UNIT.ALL_SHARES:
      // always OK
      break;

    default:
      // should never happen, but just in case...
      fund.grossAmountError = 'Select $, %, or shares';
      break;
  }
}

/**
 * Applies exchange to validation and clears or populates sell related errors: grossAmountError,
 *
 * @param {ExchangeFund} fund
 */
export function validateExchangeToFund(
  fund,
  isExchangeAmountWithinIRAContributionLimits,
  isFullyContributed,
  collegeFundsTowardsPlanMax
) {
  const {
    formattedEntityNames,
    grossAmount,
    isIRAContribution,
    productType,
    selected,
  } = fund || {};

  delete fund.grossAmountError;

  // Check for
  // 1. Is fully contributed IRA [US100786]
  // 2. Exchange From amount exceeds IRA Limits [US100784]
  if (
    isIRAContribution &&
    (isFullyContributed || !isExchangeAmountWithinIRAContributionLimits)
  ) {
    fund.disabled = true;
  }
  if (!selected || !grossAmount) {
    return;
  }

  if (selected && grossAmount < 0) {
    fund.grossAmountError = 'Exchange To amount must be positive';
  }

  // Make sure college funds are less than the beneficiary max
  if (isProductType529(productType)) {
    const { max } = collegeMaxPlanLimits[productType] || 500000;
    const planTotal =
      grossAmount +
      (collegeFundsTowardsPlanMax[formattedEntityNames][productType] || 0);
    if (planTotal > max) {
      fund.grossAmountError = `Beneficiary max is ${currency(max)}`;
    }
  }
}

export function validateExchangeToFundList(
  exchangeFromFirstFund,
  exchangeFromTotalDollars,
  exchangeType,
  exchangeToFundList,
  exchangeTransactionLimits,
  exchangeToNewTransactionLimits
) {
  // get all exchange to funds that have specified amounts inputted
  const exchangeToWithAmountsFundList =
    exchangeToFundList.filter(isSelectedWithAmount);
  // IF EXCHANGING INTO TO ONE FUND
  if (
    exchangeToWithAmountsFundList.length === 0 ||
    exchangeType !== ExchangeType.ONE_TO_MANY
  ) {
    // if the exchange to fund is a new iclass fund will need to validate amount passed
    const selectedIClassIndex = exchangeToFundList.findIndex(
      (fund) => isNewIClassFund(fund) && fund.selected
    );

    if (selectedIClassIndex > -1) {
      const selectedIClassFund = exchangeToFundList[selectedIClassIndex];

      if (exchangeFromTotalDollars < NEW_ICLASS_FUND_MIN_AMOUNT) {
        selectedIClassFund.grossAmountError = `I Class shares require a minimum balance of ${currency(
          NEW_ICLASS_FUND_MIN_AMOUNT
        )}.`;
      }
    }

    return exchangeToFundList;
  }

  const exchangeToTotalUnitless = parseFloat(
    sumArray(exchangeToWithAmountsFundList, (fund) =>
      fund.grossUnit === UNIT.ALL_REMAINING ? 0 : fund.grossAmount
    ).toFixed(2)
  );
  const unitCounts = {};
  exchangeToWithAmountsFundList.forEach(
    (fund) =>
      (unitCounts[fund.grossUnit] = coalesce(unitCounts[fund.grossUnit], 0) + 1)
  );

  if (unitCounts[UNIT.ALL_REMAINING] > 1) {
    exchangeToWithAmountsFundList.forEach((fund) => {
      if (fund.grossUnit === UNIT.ALL_REMAINING) {
        const words = getWordsForFund(fund);
        fund.grossAmountError = `All remaining can only be selected for one ${words.fund}.`;
      }
    });
    return exchangeToFundList;
  }

  const hasDollars = unitCounts[UNIT.DOLLARS];
  const hasPercent = unitCounts[UNIT.PERCENT];
  const hasAllRemaining = unitCounts[UNIT.ALL_REMAINING];

  if (hasDollars && hasPercent) {
    exchangeToWithAmountsFundList.forEach((fund) => {
      if (fund.grossUnit === UNIT.DOLLARS || fund.grossUnit === UNIT.PERCENT) {
        fund.grossAmountError = 'Amount types must match.';
      }
    });
    return exchangeToFundList;
  }

  if (hasAllRemaining && !hasDollars && !hasPercent) {
    return exchangeToFundList;
  }

  let sumOfSelectedFunds;
  // Calculate the sum of funds with values in order to determine the dollar amount of the hasAllRemaining fund
  if (hasAllRemaining) {
    sumOfSelectedFunds = sumArray(exchangeToWithAmountsFundList, (fund) =>
      fund.grossUnit === UNIT.ALL_REMAINING
        ? 0
        : getExchangeToDollarValue(exchangeFromTotalDollars, fund)
    );
  }

  exchangeToWithAmountsFundList.forEach((fund) => {
    const exchangeMinimum = getExchangeMinimum(
      exchangeTransactionLimits,
      exchangeFromFirstFund,
      fund,
      !fund.currentYearLimit
        ? exchangeToNewTransactionLimits
        : fund.currentYearLimit.find((ele) => ele.type === EXCHANGE)
    );
    const grossAmountDollars = getExchangeToDollarValue(
      exchangeFromTotalDollars,
      fund,
      sumOfSelectedFunds
    );

    if (
      hasDollars &&
      hasAllRemaining &&
      grossAmountDollars < exchangeMinimum &&
      exchangeToTotalUnitless !== exchangeFromTotalDollars
    ) {
      fund.grossAmountError = `Please adjust your exchange allocation amounts so that they total ${currency(
        exchangeFromTotalDollars
      )} or edit your original allocation amount.`;
    }
    if (hasDollars) {
      if (
        grossAmountDollars >= exchangeMinimum &&
        exchangeToTotalUnitless !== exchangeFromTotalDollars &&
        !hasAllRemaining
      ) {
        fund.grossAmountError = `Please adjust your exchange allocation amounts so that they total ${currency(
          exchangeFromTotalDollars
        )} or edit your original allocation amount.`;
      }

      if (
        hasAllRemaining &&
        exchangeToTotalUnitless === exchangeFromTotalDollars
      ) {
        if (fund.grossUnit === UNIT.ALL_REMAINING) {
          fund.grossAmountError = 'The exchange amount is equivalent to $0.00.';
        }
      }
    }

    if (grossAmountDollars < exchangeMinimum && grossAmountDollars > 0) {
      fund.grossAmountError = isNewIClassFund(fund)
        ? `I Class shares require a minimum balance of ${currency(
            NEW_ICLASS_FUND_MIN_AMOUNT
          )}.`
        : `Amount must be greater than ${currency(exchangeMinimum)}.`;
    }
    if (hasPercent) {
      // must have percentages or percentages + all remaining
      if (
        exchangeToTotalUnitless > 100 ||
        (exchangeToTotalUnitless < 100 && !hasAllRemaining)
      ) {
        fund.grossAmountError = `Exchange To percentages must total 100%.`;
      }
      if (hasAllRemaining && exchangeToTotalUnitless === 100) {
        if (fund.grossUnit === UNIT.ALL_REMAINING) {
          fund.grossAmountError = 'The exchange amount is equivalent to $0.00.';
        }
      }
    }
  });
  return exchangeToFundList;
}
